import { useCallback, useEffect, useRef } from 'react'
import { useStore } from '@/store'
import { chatRequest } from '@/lib/support-chat/client'

/** Cancel reads on unmount, and never consume a reply for another conversation. */
export function useConversationRequest(browserContext: string) {
  const customerId = useStore(state => state.user?.id)
  const lifetime = useRef<AbortController | null>(null)
  useEffect(() => {
    const controller = new AbortController()
    lifetime.current = controller
    return () => controller.abort()
  }, [browserContext, customerId])

  return useCallback(
    async <T>(options: Parameters<typeof chatRequest<T>>[0] = {}) => {
      const controller = lifetime.current
      function requireCurrentConversation() {
        const account = useStore.getState()
        if (
          !controller ||
          controller.signal.aborted ||
          !account.authResolved ||
          account.user?.id !== customerId
        ) {
          throw new Error('Open this conversation again before continuing.')
        }
      }
      requireCurrentConversation()
      const signal = options.signal
        ? AbortSignal.any([options.signal, controller!.signal])
        : controller!.signal
      const response = await chatRequest<T>({
        ...options,
        browserContext,
        signal,
      })
      requireCurrentConversation()
      if (response.browserContext !== browserContext) {
        throw new Error(
          'The conversation changed. Open chat again to continue.'
        )
      }
      return response
    },
    [browserContext, customerId]
  )
}
